import { addMonths, format, startOfMonth } from 'date-fns';

/*
 Loan step by step

 Generate interest
 Pay min payment
 *Pay interest
 *Pay principal
 If leftover money, add to extra_payment
 Remove loans at 0
 Apply extra payment to loans until extra payment is 0 or remaining_loans is 0
 Sort based on payment type
 Remove loans as paid
 Increment month

 Loop above until remaining loans.size is 0

 */

export class ResultsController {
  // initialize loan_results with empty loans and totals
  static loan_results = { loans: {}, totals: {} };

  constructor() {}

  // dictionaries and arrays are passed by reference
  // .add(1,'months');
  // .format("MMMM YYYY")
  // need to move current_interest to current_principal
  static calculate(loans, payment_type, monthly_payment, minimum_payments_only = false) {
    ResultsController.loan_results = { loans: {}, totals: {} };
    const remaining_loans = {};
    const current_principal = {};
    const current_interest = {};
    const minimum_payments = {};

    // initialize each loan's rows with current date, and balance remaining
    let month = startOfMonth(new Date());
    for (let loan_key in loans) {
      ResultsController.loan_results.loans[loan_key] = {
        rows: [],
        loan_name: loans[loan_key].loanName,
        total_date: '',
        total_interest_paid: 0,
        id: loan_key,
        starting_balance: loans[loan_key].currentBalance,
      };
      current_principal[loan_key] = loans[loan_key].currentBalance;
      current_interest[loan_key] = 0;
      remaining_loans[loan_key] = loans[loan_key];
      ResultsController.log_default_line(loan_key, month);
      const current_balance = current_principal[loan_key] + current_interest[loan_key];
      ResultsController.log_balance_remaining_line(loan_key, current_balance);
    }

    // add interest to each loan so that the first month's payment is accurate!!!
    ResultsController.add_interest(remaining_loans, current_principal, current_interest);

    let counter = 0;
    while (Object.keys(remaining_loans).length > 0) {
      counter++;
      if (counter > 12 * 122) return null;

      month = addMonths(month, 1);
      let extra_payment = monthly_payment;
      for (let loan_key in remaining_loans) {
        minimum_payments[loan_key] = remaining_loans[loan_key].minimumPayment;
        extra_payment = extra_payment - minimum_payments[loan_key];
        ResultsController.log_default_line(loan_key, month);
      }
      // pay minimums first
      ResultsController.pay_minimums(
        remaining_loans,
        current_principal,
        current_interest,
        minimum_payments
      );
      extra_payment = extra_payment + ResultsController.add_leftover_payments(minimum_payments);
      ResultsController.remove_paid_off_loans(
        remaining_loans,
        current_principal,
        current_interest,
        minimum_payments
      );
      if (!minimum_payments_only) {
        ResultsController.apply_extra_payments(
          remaining_loans,
          current_principal,
          current_interest,
          payment_type,
          extra_payment,
          minimum_payments
        );
      }
      for (let loan_key in remaining_loans) {
        const current_balance = current_principal[loan_key] + current_interest[loan_key];
        ResultsController.log_balance_remaining_line(loan_key, current_balance);
      }
      ResultsController.add_interest(remaining_loans, current_principal, current_interest);
    }
    ResultsController.log_totals(month);

    return ResultsController.loan_results;
  }

  static remove_paid_off_loans(
    remaining_loans,
    current_principal,
    current_interest,
    minimum_payments
  ) {
    for (let loan_key in remaining_loans) {
      const current_balance = current_principal[loan_key] + current_interest[loan_key];
      if (current_balance === 0) {
        ResultsController.remove_loan(
          remaining_loans,
          current_principal,
          current_interest,
          minimum_payments,
          loan_key
        );
      }
    }
  }

  static remove_loan(
    remaining_loans,
    current_principal,
    current_interest,
    minimum_payments,
    loan_key
  ) {
    delete remaining_loans[loan_key];
    delete current_principal[loan_key];
    delete current_interest[loan_key];
    delete minimum_payments[loan_key];
  }

  static pay_minimums(remaining_loans, current_principal, current_interest, minimum_payments) {
    for (let loan_key in remaining_loans) {
      minimum_payments[loan_key] = ResultsController.apply_payment(
        loan_key,
        minimum_payments[loan_key],
        current_interest,
        current_principal
      );
    }
  }

  static apply_payment(id, payment, interest_dict, principal_dict) {
    // interest greater than or equal to payment
    if (interest_dict[id] >= payment) {
      ResultsController.log_interest_paid_line(id, payment);
      interest_dict[id] = interest_dict[id] - payment;
      return 0;
    }
    // payment greater than interest left
    else {
      ResultsController.log_interest_paid_line(id, interest_dict[id]);
      const remaining_payment = payment - interest_dict[id];
      interest_dict[id] = 0;
      // apply to principal
      return ResultsController.principal_apply_payment(id, remaining_payment, principal_dict);
    }
  }

  static principal_apply_payment(id, payment, principal_dict) {
    // principal greater than or equal to payment
    if (principal_dict[id] >= payment) {
      ResultsController.log_principal_paid_line(id, payment);
      principal_dict[id] = principal_dict[id] - payment;
      return 0;
    }
    // payment greater than principal left
    else {
      ResultsController.log_principal_paid_line(id, principal_dict[id]);
      const remaining_payment = payment - principal_dict[id];
      principal_dict[id] = 0;
      return remaining_payment;
    }
  }

  // assuming monthly
  static add_interest(remaining_loans, current_principal, current_interest) {
    for (let loan_key in remaining_loans) {
      const current_balance = current_principal[loan_key] + current_interest[loan_key];
      // assuming monthly
      const interest_generated =
        current_balance * (remaining_loans[loan_key].interestRate / 100 / 12);
      current_interest[loan_key] =
        current_interest[loan_key] + ResultsController.precise_round(interest_generated, 2);
    }
  }

  static apply_extra_payments(
    remaining_loans,
    current_principal,
    current_interest,
    payment_type,
    extra_payment,
    minimum_payments
  ) {
    const sorted_keys = ResultsController.sort_loans(
      remaining_loans,
      current_principal,
      current_interest,
      payment_type
    );
    for (let key in sorted_keys) {
      if (extra_payment === 0 || Object.keys(remaining_loans).length === 0) {
        return extra_payment;
      }
      extra_payment = ResultsController.apply_payment(
        sorted_keys[key],
        extra_payment,
        current_interest,
        current_principal
      );
      if (current_principal[sorted_keys[key]] + current_interest[sorted_keys[key]] === 0) {
        ResultsController.remove_loan(
          remaining_loans,
          current_principal,
          current_interest,
          minimum_payments,
          sorted_keys[key]
        );
      }
    }
  }

  static add_leftover_payments(minimum_payments) {
    let leftover = 0;
    for (let key in minimum_payments) {
      leftover += minimum_payments[key];
    }
    return leftover;
  }

  static precise_round(num, decimals) {
    const t = Math.pow(10, decimals);
    return parseFloat(
      (
        Math.round(
          num * t + (decimals > 0 ? 1 : 0) * (Math.sign(num) * (10 / Math.pow(100, decimals)))
        ) / t
      ).toFixed(decimals)
    );
  }

  static sort_loans(remaining_loans, current_principal, current_interest, payment_type) {
    if (payment_type === 'snowball') {
      return Object.keys(remaining_loans).sort(function (a, b) {
        return (
          current_interest[a] + current_principal[a] - (current_interest[b] + current_principal[b])
        );
      });
    } else if (payment_type === 'avalanche') {
      return Object.keys(remaining_loans).sort(function (a, b) {
        return remaining_loans[b].interestRate - remaining_loans[a].interestRate;
      });
    } else {
    }
  }

  // loan_results = { "0": [line, line], "1": [line, line] }
  // line = {month, payment,principal_paid,interest_paid,balance_remaining}
  static log_default_line(id, date) {
    ResultsController.loan_results.loans[id].rows.push({});
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['date'] = format(date, 'MMMM yyyy');
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['payment'] = 0;
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['principal_paid'] = 0;
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['interest_paid'] = 0;
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['balance_remaining'] = 0;
  }

  static log_payment_line(id, payment) {
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['payment'] += ResultsController.precise_round(payment, 2);
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['payment'] = ResultsController.precise_round(
      ResultsController.loan_results.loans[id].rows[
        ResultsController.loan_results.loans[id].rows.length - 1
      ]['payment'],
      2
    );
  }

  static log_principal_paid_line(id, principal_paid) {
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['principal_paid'] = ResultsController.precise_round(
      ResultsController.loan_results.loans[id].rows[
        ResultsController.loan_results.loans[id].rows.length - 1
      ]['principal_paid'] + principal_paid,
      2
    );
    ResultsController.log_payment_line(id, principal_paid);
  }

  static log_interest_paid_line(id, interest_paid) {
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['interest_paid'] = ResultsController.precise_round(
      ResultsController.loan_results.loans[id].rows[
        ResultsController.loan_results.loans[id].rows.length - 1
      ]['interest_paid'] + interest_paid,
      2
    );
    ResultsController.log_payment_line(id, interest_paid);
  }

  static log_balance_remaining_line(id, balance_remaining) {
    ResultsController.loan_results.loans[id].rows[
      ResultsController.loan_results.loans[id].rows.length - 1
    ]['balance_remaining'] += ResultsController.precise_round(balance_remaining, 2);
  }

  static log_totals(date) {
    ResultsController.loan_results.totals['total_date'] = format(date, 'MMMM yyyy');
    let total_interest_paid = 0;
    for (let loan_key in ResultsController.loan_results.loans) {
      ResultsController.loan_results.loans[loan_key].total_interest_paid = 0;
      for (let line_key in ResultsController.loan_results.loans[loan_key].rows) {
        ResultsController.loan_results.loans[loan_key].total_interest_paid +=
          ResultsController.loan_results.loans[loan_key].rows[line_key]['interest_paid'];
      }
      ResultsController.loan_results.loans[loan_key].total_interest_paid =
        ResultsController.precise_round(
          ResultsController.loan_results.loans[loan_key].total_interest_paid,
          2
        );
      ResultsController.loan_results.loans[loan_key].total_date =
        ResultsController.loan_results.loans[loan_key].rows[
          ResultsController.loan_results.loans[loan_key].rows.length - 1
        ]['date'];
      total_interest_paid += ResultsController.loan_results.loans[loan_key].total_interest_paid;
    }
    ResultsController.loan_results.totals['total_interest_paid'] = ResultsController.precise_round(
      total_interest_paid,
      2
    );
  }
}
